import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, Eye, EyeOff } from 'lucide-react';
import type { MyRankingResponse } from '@/types/session.ts';

const HIDDEN_KEY = 'progress.rankingHidden';
type Scope = 'team' | 'company';

interface Props {
  ranking: MyRankingResponse;
}

export default function RankingCard({ ranking }: Props) {
  const { t } = useTranslation();

  // Use loose null/undefined check (`!= null`) so the card hides cleanly even
  // when the backend returns the old DTO shape (no team/company fields) and
  // doesn't show empty "Posición # de" placeholders.
  const hasTeam = ranking.teamPosition != null && ranking.teamTotal != null;
  const hasCompany = ranking.companyPosition != null && ranking.companyTotal != null;

  // Backend hidden flag → no rendering at all (e.g. solo tu en la empresa).
  if (ranking.hidden || (!hasTeam && !hasCompany)) return null;

  // Default tab: team if available, otherwise company.
  const initialScope: Scope = hasTeam ? 'team' : 'company';
  const [scope, setScope] = useState<Scope>(initialScope);

  // User-toggleable hidden state — persists in localStorage so the choice
  // survives reloads. Empty/invalid value defaults to false.
  const [hidden, setHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(HIDDEN_KEY, hidden ? '1' : '0');
    } catch {
      /* localStorage unavailable — non-fatal */
    }
  }, [hidden]);

  const tierClass =
    ranking.tier === 'elite'
      ? 'bg-yellow-500/10 text-yellow-600'
      : ranking.tier === 'pro'
        ? 'bg-purple-500/10 text-purple-600'
        : 'bg-gray-500/10 text-gray-500';
  const tierLabel =
    ranking.tier === 'elite'
      ? t('progress.tierElite')
      : ranking.tier === 'pro'
        ? t('progress.tierPro')
        : t('progress.tierStandard');

  const showTabs = hasTeam && hasCompany;
  const position = scope === 'team' ? ranking.teamPosition! : ranking.companyPosition!;
  const total = scope === 'team' ? ranking.teamTotal! : ranking.companyTotal!;
  const scopeLabel =
    scope === 'team'
      ? t('progress.inYourTeam', { defaultValue: 'en tu equipo' })
      : t('progress.inYourCompany', { defaultValue: 'en tu compañía' });

  return (
    <div className="relative rounded-xl border border-border bg-card p-4 text-center">
      {/* Blur overlay con botón Mostrar centrado, mismo tamaño que el card */}
      {hidden && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-card/40 backdrop-blur-md">
          <button
            type="button"
            onClick={() => setHidden(false)}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
          >
            <Eye className="h-3.5 w-3.5" />
            {t('progress.rankingShow', { defaultValue: 'Mostrar' })}
          </button>
        </div>
      )}

      <div className={`mb-2 flex items-center justify-between gap-2 ${hidden ? 'pointer-events-none' : ''}`}>
        {showTabs ? (
          <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setScope('team')}
              className={`rounded px-2 py-1 font-medium transition-colors ${
                scope === 'team' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('progress.tabTeam', { defaultValue: 'Equipo' })}
            </button>
            <button
              type="button"
              onClick={() => setScope('company')}
              className={`rounded px-2 py-1 font-medium transition-colors ${
                scope === 'company' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('progress.tabCompany', { defaultValue: 'Compañía' })}
            </button>
          </div>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => setHidden(true)}
          className="text-muted-foreground transition-colors hover:text-foreground"
          aria-label={t('progress.rankingHide', { defaultValue: 'Ocultar ranking' })}
          title={t('progress.rankingHide', { defaultValue: 'Ocultar ranking' })}
        >
          {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      <div className="flex items-center justify-center gap-2">
        <Trophy className="h-4 w-4 text-yellow-500" />
        <span className="text-sm font-semibold text-foreground">
          {t('progress.positionOf', { position, total })}
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{scopeLabel}</p>
      <span className={`mt-2 inline-block rounded-full px-3 py-0.5 text-xs font-bold ${tierClass}`}>
        {tierLabel}
      </span>
    </div>
  );
}
